'use client';

import { lazy, Suspense, type ReactNode } from 'react';
import styles from './shelf.module.css';

export { styles as shelf };

/**
 * The library: a shelf of game cases you can take one off.
 *
 * **This is not a grid of capsules and must not become one.** The library drew
 * `CapsuleGrid` — the shop's own component, the shop's own art, the shop's own
 * hover — so the page showing what somebody owns was the page showing what they
 * could buy with a different heading on it. A shop sells listings and a library
 * holds objects, and the difference is worth a different shape.
 *
 * ## It is rendered, not drawn
 *
 * The scene is three.js through `@react-three/fiber`, and it got there the long
 * way: four passes at folding a DVD case out of CSS transforms, each one coming
 * back looking like coloured rectangles hinged together. Which is what it was.
 * **CSS has no lights.** A box reads as a box because of what the light does to
 * its edges, and the one trick that fakes that in CSS is a gradient — the thing
 * section 04 forbids on every surface in this product. So the case is a real
 * object in a real scene, and the shading is computed rather than drawn.
 *
 * See `case-scene.tsx`, which is the whole of it.
 *
 * ## Loaded only by the people who open it
 *
 * three.js is the largest thing in this bundle by a distance, and the library is
 * one route behind a sign-in. So the scene is a lazy import and nothing else
 * pulls it in: a visitor reading the shop, the legal pages or a game's page
 * never downloads a renderer. `Suspense` covers the gap.
 *
 * ## Presentational, deliberately
 *
 * Nothing here fetches anything, knows what a session is, or can tell whether
 * the launcher is running. It takes cases and calls back when one is pressed;
 * what an open case says comes in as `detail`, and the one thing to do with it
 * comes in as a node. Those facts come from three different places — the
 * catalogue for the art, `/me/library/:slug` for the hours, the Tauri bridge for
 * what is installed — and a component that reached for them itself would be a
 * screen rather than a piece of furniture. `routes/library.tsx` is the screen.
 */

export interface ShelfCase {
  slug: string;
  name: string;
  /** The cover art, or null for a title nobody has uploaded any for. */
  coverUrl: string | null;
  /**
   * What the disc is printed with. Cropped square by `useDiscLabel`.
   *
   * Its own field rather than the cover reused, because a game with a real disc
   * label should use it — the cover is the fallback the screen chooses, not a
   * rule this component imposes.
   */
  discUrl?: string | null;
  /**
   * The platform whose logo goes on the spine band and the disc.
   *
   * A key into `icons/platform-marks`, so `'steam'` rather than a URL — the
   * marks are path data drawn into a canvas rather than images fetched, which
   * is what lets the spine texture carry one at all. Null leaves the band a
   * plain block, which is right for a game that is not on a store yet.
   */
  mark?: string | null;
  /**
   * Whether this is still yours.
   *
   * Hard rule 19: never a penalty, so this is always a refund, a chargeback, a
   * closed wave or an unclaimed gift. It stays on the shelf and goes grey —
   * removing it would be the version of this screen that quietly disagrees with
   * the receipt.
   */
  gone: boolean;
}

/** One shop the game is on, and where. */
export interface CaseShop {
  /** A key into `icons/platform-marks`, for the logo on the button. */
  platform: string;
  /** What to call it, decided by the screen. See the note on this component. */
  label: string;
  url: string;
}

/**
 * Everything an open case has to say. This is the game's page.
 *
 * ── It used to be a receipt, and now it is a listing ──────────────────────
 *
 * The fields were `studio`, `figures`, `installed`, `installing`, `running` — a
 * library's questions, because this component was a library: how long you had
 * played it, which build was on the machine, whether it was running. There is
 * no library and no launcher, so every one of them was either empty or a
 * guess dressed as a measurement.
 *
 * What replaces them is what a person opening a case actually wants: what the
 * game is, how you play it, what it looks like, and where to buy it. The name
 * and the studio are deliberately absent — the name is printed across the disc
 * on the other leaf, and the studio is us on every case in the room.
 */
export interface OpenCaseDetail {
  /** One line, above the description. */
  tagline: string;
  /** Out now, coming soon, in development. */
  status: string;
  /** The longer form of the same fact, where a date belongs. */
  releaseLine: string;
  /**
   * How you play it: at most three badges, each with a picture.
   *
   * Decided by the screen rather than handed over raw. This used to be Steam's
   * category strings and the genres beside them, printed as a row of eight
   * chips — a list nobody reads, in which the two facts somebody wants are
   * lost among the six they do not.
   */
  modes: readonly { label: string; icon: string }[];
  /**
   * The opening paragraph, for the leaflet.
   *
   * Its own field rather than `description[0]` read at the far end, because the
   * leaflet and the reader are showing deliberately different things: the case
   * carries the lede and the sheet carries the lot. A component slicing the
   * array itself would be deciding that, and it is the screen's decision.
   */
  lede: string;
  /**
   * Whether there is anything the leaflet is not showing.
   *
   * **Decided from the copy, not measured from the layout.** It was measured
   * once — `scrollHeight` against `clientHeight` on the body — and it never
   * fired: `Html` renders its children into a *separate React root*, so the
   * ref was still null when the effect ran and the button never appeared. The
   * leaflet is a fixed box, so what fits is knowable without asking the DOM.
   */
  hasMore: boolean;
  /** What it is, in paragraphs. The reader shows all of them. */
  description: readonly string[];
  /** The handful of things it does, one line each. */
  features: readonly string[];
  /** Screenshots, biggest first. The insert shows them as a strip. */
  shots: readonly string[];
  /** Every shop it is on, in a settled order. */
  shops: readonly CaseShop[];
}

export interface GameLibraryProps {
  cases: readonly ShelfCase[];
  /**
   * Drawn over the room, above the wall and the stage.
   *
   * Here rather than beside the room, because the room is `position: relative`
   * with `isolation: isolate` and its own two layers — anything stacked on it
   * from outside is stacking against a different context and lands behind the
   * canvas or in front of the open case, depending on the browser.
   */
  title?: ReactNode;
  smallPrint?: ReactNode;
  /** Which case has been taken off the pile, or null. */
  openSlug: string | null;
  detail: OpenCaseDetail | null;
  /**
   * The backdrop behind the table, or null for the plain room.
   *
   * Chosen in the admin panel, the same way the shop's is (#183). Passed in
   * rather than fetched, for the reason the rest of this component is
   * presentational: the screen knows what is configured and this draws it.
   */
  wallUrl?: string | null | undefined;
  onOpen: (slug: string) => void;
  onShut: () => void;
}

const CaseScene = lazy(() => import('./case-scene'));

export function GameLibrary(props: GameLibraryProps): React.ReactElement {
  return (
    <Suspense fallback={<div className={styles.roomWaiting ?? ''} aria-hidden="true" />}>
      <CaseScene {...props} />
    </Suspense>
  );
}
